import { Injectable, OnDestroy } from '@angular/core';
import { HttpTransportType, HubConnection, IHttpConnectionOptions } from '@microsoft/signalr';
import { Observable, Subject, Subscription } from 'rxjs';
import { appSettings } from '@nha/config';
import { SignalRHubBase } from '@nha/api/shared';

/**
 * Connects to the Notifications.Api NotificationsHub. The server joins each connection to a
 * per-user group on connect, then invokes `NewNotifications` with the number of notifications
 * just created for that user over the in-app channel — so consumers only ever hear about their
 * own notifications.
 *
 * Drives its own lifecycle from UserSessionService.tokenSubject — connects when a token appears,
 * disconnects when it goes null. Injecting the service (the notification menu store does this
 * when the bell mounts) is all the wiring an app needs.
 */
@Injectable({ providedIn: 'root' })
export class NotificationsHubService extends SignalRHubBase implements OnDestroy {
	private _newNotifications = new Subject<number>();
	private _reconnected = new Subject<void>();

	/** Emits the count of notifications just created for the current user. */
	newNotifications$: Observable<number> = this._newNotifications.asObservable();

	/**
	 * Emits after the transport recovers from a drop. Pushes sent while offline are gone — the
	 * server only addresses live connections — so consumers should re-sync state on this signal.
	 */
	reconnected$: Observable<void> = this._reconnected.asObservable();

	private readonly _tokenSub: Subscription = this.session.tokenSubject.subscribe((token) => {
		if (!appSettings.hubs?.notifications) {
			console.warn('NotificationsHubService: hubs.notifications is not configured; realtime notifications are disabled.');
			return;
		}

		if (token) {
			this.connect();
		} else {
			this.disconnect();
		}
	});

	protected get hubUrl(): string {
		return `${appSettings.hubs.notifications}notifications`;
	}

	/**
	 * WebSockets-only for the same reason as GradebookLockHubService: the load-balanced API has
	 * no session affinity, so the negotiate flow (negotiate → connect must hit the same instance)
	 * is non-viable. A WebSocket is inherently pinned to one instance and the backplane fans
	 * messages across the rest.
	 */
	protected override get connectionOptions(): IHttpConnectionOptions {
		return {
			...super.connectionOptions,
			skipNegotiation: true,
			transport: HttpTransportType.WebSockets
		};
	}

	protected registerHandlers(conn: HubConnection): void {
		conn.on(
			'NewNotifications',
			this.zoned((newNotificationCount: number) => this._newNotifications.next(newNotificationCount))
		);

		conn.onreconnected(this.zoned(() => this._reconnected.next()));
	}

	ngOnDestroy(): void {
		this._tokenSub.unsubscribe();
		this.disconnect();
	}
}
